/**
 * B6 · "Mi ruta" — the student's levels path.
 *
 * The entire page is gated by `academy.progression.path`: turned off, the student
 * goes back to `/learn` with the usual list and **loses nothing** — the engine
 * keeps computing state, certificates keep being issued and the
 * record doesn't depend on the map.
 */
import { createMemo, createSignal, For, Show } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { Route as RouteIcon } from "@fleet/icons";
import { withFeatureGate } from "@/features/flags/feature-gate";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer, PageHeader } from "@fleet/ui";
import { PillTabs } from "@/components/ui/pill-tabs";
import { PathMap } from "@/features/progression/path-map";
import { NextStageOffers } from "@/features/progression/next-stage-offer";
import { useTracks } from "@/features/progression/use-progression";
import { PROGRESSION_PATH_FEATURE } from "@/features/progression/api";
import { appRoute } from "./route";

function PathPage() {
  const { t } = useI18n();
  const tracks = useTracks();
  const [selected, setSelected] = createSignal<string | undefined>();

  const available = createMemo(() => tracks.data?.tracks ?? []);
  const activeTrackId = createMemo(() => selected() ?? available()[0]?.id);

  return (
    <PageContainer maxWidth="2xl">
      <PageHeader
        title={t(msg`My path`)}
        description={t(
          msg`Your levels, in order. Finish one to open the next — and see exactly what's missing when one is locked.`,
        )}
        icon={RouteIcon}
      />

      {/* The offer goes ABOVE and persists: it isn't a modal that closes and
          gets lost. It lives here and in /payments until it expires or is dismissed. */}
      <div class="mt-4">
        <NextStageOffers />
      </div>

      <Show when={available().length > 1}>
        <div class="mt-4">
          <PillTabs
            tabs={available().map((track) => ({ id: track.id, label: track.name }))}
            value={activeTrackId() ?? ""}
            onChange={(id) => setSelected(id)}
            testIdPrefix="progression-track"
            label={t(msg`Learning paths`)}
          />
        </div>
      </Show>

      <div class="mt-6">
        <Show when={activeTrackId()}>{(id) => <PathMap trackId={id()} />}</Show>
      </div>

      {/* Legend: reading the path can't depend on distinguishing
          colors, so the states are listed with their icon and their name. */}
      <ul class="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <For
          each={[
            t(msg`Locked — something is missing`),
            t(msg`Open — you can start`),
            t(msg`In progress`),
            t(msg`Completed`),
            t(msg`Certified`),
          ]}
        >
          {(label) => <li>{label}</li>}
        </For>
      </ul>
    </PageContainer>
  );
}

export const pathRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/path",
  component: withFeatureGate(PROGRESSION_PATH_FEATURE, PathPage),
});
